/* eslint-disable max-len */
import React, { FC, useEffect, useMemo, useRef, useState } from 'react'
import Modal from '../common/Modal'
import { dateToTodayElapsedSeconds, secToHour, secToHourMinute, secToMinute } from '../../../utils/date'
import Tips from '../common/Tips'
import { uniqueId } from 'lodash'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { IconName, IconPrefix } from '@fortawesome/fontawesome-common-types'
import LoadingSquare from '../common/LoadingSquare'

interface DetailList {
  start_time: string
  end_time: string
  service_name: string | null
  task_name: string | null
  time: number
  icon: string
  icon_color: string
  labeled: boolean
}

interface ApplicationList {
  service_name: string
  task_name: string | null
  icon: string
  icon_color: string
  time: number
  list: {
    start_sec: number
    end_sec: number
    time: number
  }[]
  labeled: boolean
}

interface Props {
  timeList: {
    start_time: string
    end_time: string
  }[],
  originTask: DetailList[]
  closeCallback: VoidFunction
  isDisplay: boolean
  date: string
  loading: boolean
}

const NotWorkingModalForDisplay: FC<Props> = (props) => {
  const {
    timeList,
    originTask,
    closeCallback,
    isDisplay,
    date,
    loading,
  } = props

  const TIME_LIST = Array(25).fill(null).map((_v, i) => `${i + 5}:00`)

  const [cutTimeList, setCutTimeList] = useState<{startSec: number, endSec: number}[]>([])

  const [error, setError] = useState<{key: string, text: string}[]>([])
  const [notWorkingApplicationList, setNotWorkingApplicationList] = useState<ApplicationList[]>([])

  const clickRef = useRef() as React.MutableRefObject<HTMLDivElement>

  const customOriginTask = useMemo(() => {
    if (!originTask) return []
    return originTask.map((v) => ({ ...v, start_sec: dateToTodayElapsedSeconds(v.start_time, date), end_sec: dateToTodayElapsedSeconds(v.end_time, date) }))
  }, [originTask])

  const buildDisplayTime = (diffSeconds: number) => {
    if (diffSeconds > 0 && diffSeconds < 60) {
      return "1分未満"
    }
    if (secToHour(diffSeconds)) {
      return `${secToHour(diffSeconds)}時間${secToMinute(diffSeconds)}分`
    }
    return `${secToMinute(diffSeconds)}分`
  }

  const resetParam = () => {
    setError([])
    setCutTimeList([])
    setNotWorkingApplicationList([])
  }

  const closeModal = () => {
    resetParam()
    closeCallback()
  }

  const rulerBlock = (custom = true, height = 42) => (
    <div
      className="timeline_ruler_area"
      style={{
        width: custom ? "100%" : "",
        marginLeft: custom ? 0 : "",
        position: "absolute",
        top: 0,
        left: 0,
        height: `${height}px`,
      }}
    >
      {
        TIME_LIST.map((v) => (
          <div key={`ruler_${v}`} />
        ))
      }
      <div />
    </div>
  )

  const labelBlock = () => (
    <div
      className="timeline_label_area"
      style={{
        width: "100%",
        position: "relative",
        height: "14px",
        marginTop: "4px",
        marginBottom: "4px",
        fontSize: "10px",
        color: "#666666",
      }}
    >
      {
        TIME_LIST.map((v, i) => (
          <div
            key={v}
            style={{
              position: "absolute",
              left: `${(i / 24) * 100}%`,
              transform: "translateX(-50%)",
            }}
          >
            {v}
          </div>
        ))
      }
    </div>
  )

  const buildBlock = (targetStartSec: number, targetEndSec: number) => {
    const startPosition = ((targetStartSec - 18000) / 86400) * 100
    const blockWidth = ((targetEndSec - targetStartSec) / 86400) * 100
    return (
      <>
        <div
          style={{
            position: "absolute",
            left: `${startPosition}%`,
            top: "50%",
            width: `${blockWidth}%`,
            height: "44px",
            padding: 0,
            backgroundColor: "rgba(0, 19, 43, 0.5)",
            transform: "translateY(-50%)",
            zIndex: 1,
          }}
        >
          <Tips
            description={`${secToHourMinute(targetStartSec)} ~ ${secToHourMinute(targetEndSec)}`}
            className=""
            icon={false}
          >
            <></>
          </Tips>
        </div>
      </>
    )
  }

  useEffect(() => {
    if (timeList.length) {
      setCutTimeList(timeList.map((v) => ({ startSec: dateToTodayElapsedSeconds(v.start_time, date), endSec: dateToTodayElapsedSeconds(v.end_time, date) })))
    }
  }, [timeList])

  useEffect(() => {
    if (!customOriginTask.length) return
    let tmp: ApplicationList[] = []
    cutTimeList.forEach((range) => {
      customOriginTask.forEach((data) => {
        let tmpTime = data.time
        let tmpStartSec = data.start_sec
        let tmpEndSec = data.end_sec
        let is_target = false
        if (range.startSec <= data.start_sec && range.endSec >= data.end_sec) {
          tmpTime = data.time
          tmpStartSec = data.start_sec
          tmpEndSec = data.end_sec
          is_target = true
        } else if (range.startSec <= data.start_sec && range.endSec >= data.start_sec && range.endSec <= data.end_sec) {
          tmpTime = range.endSec - data.start_sec
          tmpStartSec = data.start_sec
          tmpEndSec = range.endSec
          is_target = true
        } else if (range.startSec >= data.start_sec && range.startSec <= data.end_sec && range.endSec >= data.end_sec) {
          tmpTime = data.end_sec - range.startSec
          tmpStartSec = range.startSec
          tmpEndSec = data.end_sec
          is_target = true
        } else if (range.startSec >= data.start_sec && range.endSec <= data.end_sec) {
          tmpTime = range.endSec - range.startSec
          tmpStartSec = range.startSec
          tmpEndSec = range.endSec
          is_target = true
        }
        if (is_target) {
          if (tmp.find((v) => v.service_name === (data.service_name || "不明なアプリ") && v.task_name === data.task_name)) {
            tmp = tmp.map((v) => (v.service_name === (data.service_name || "不明なアプリ") && v.task_name === data.task_name
              ? ({ ...v, time: v.time + tmpTime, list: [...v.list, { start_sec: tmpStartSec, end_sec: tmpEndSec, time: tmpTime }] })
              : v
            ))
          } else {
            tmp.push({
              service_name: data.service_name || "不明なアプリ",
              task_name: data.task_name,
              icon: data.icon,
              icon_color: data.icon_color,
              time: tmpTime,
              list: [
                { start_sec: tmpStartSec, end_sec: tmpEndSec, time: tmpTime },
              ],
              labeled: data.labeled,
            })
          }
        }
      })
    })
    setNotWorkingApplicationList(tmp.filter((v) => v.time).sort((a, b) => a.list[0].start_sec - b.list[0].start_sec))
  }, [cutTimeList, customOriginTask])

  return (
    <>
      <Modal
        show={isDisplay}
        wrapperClass="w900"
        closeCallback={closeModal}
        closeable
      >
        <>
          {
            loading ? (
              <>
                <LoadingSquare>
                  <>
                    Now Loading…
                  </>
                </LoadingSquare>
              </>
            ) : (
              <>
                <div>
                  <div className="header_area" style={{ userSelect: "none" }}>
                    <div className="text_area">
                      <div>
                        業務外作業時間範囲が設定されています
                      </div>
                    </div>
                  </div>
                  <div className="item" style={{ userSelect: "none" }}>
                    <div className="labeling_legend_area" style={{ position: "relative", marginBottom: "8px" }}>
                      <div className="timeline_legend">
                        <div
                          className="legend_color"
                          style={{
                            backgroundColor: "#4570f3",
                          }}
                        />
                        <div className="legend_text">
                          作業データ
                        </div>
                      </div>
                      <div className="timeline_legend">
                        <div
                          className="legend_color"
                          style={{
                            backgroundColor: "rgba(0, 19, 43, 0.5)",
                          }}
                        />
                        <div className="legend_text">
                          業務外範囲
                        </div>
                      </div>
                    </div>
                    <div
                      className="timeline"
                      style={{
                        marginBottom: "0px",
                        height: "44px",
                      }}
                    >
                      <div
                        ref={clickRef}
                        className="timeline_row"
                        style={{
                          borderBottom: "none",
                          height: "42px",
                        }}
                      >
                        {rulerBlock()}
                        {
                          customOriginTask.map((value) => (
                            <div
                              key={`not_working_modal_${value.start_time}_${value.end_time}`}
                              className="timeline_bar not_working"
                              style={{
                                left: `${((value.start_sec - 18000) / 86400) * 100}%`,
                                width:
                                `${((value.end_sec - value.start_sec) / 86400) * 100 > 0.2 ? ((value.end_sec - value.start_sec) / 86400) * 100 : 0.2}%`,
                              }}
                            />
                          ))
                        }
                        {
                          cutTimeList.map((v, i) => (
                            <React.Fragment key={`range_${v.startSec}_${v.endSec}_${i + 1}`}>
                              {buildBlock(v.startSec, v.endSec)}
                            </React.Fragment>
                          ))
                        }
                      </div>
                    </div>
                    {labelBlock()}
                    {
                      error.find((v) => v.key === "range")?.text && (
                        <div className="error">
                          {error.find((v) => v.key === "range")?.text}
                        </div>
                      )
                    }
                    {
                      notWorkingApplicationList.some((v) => v.labeled) && (
                        <div
                          style={{
                            border: "solid 1px #c61516",
                            borderRadius: "4px",
                            padding: "4px",
                            color: "#c61516",
                            backgroundColor: "#ffdfdf",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            gap: "4px",
                          }}
                        >
                          指定した範囲に仕分け済み作業データが含まれています！
                        </div>
                      )
                    }
                  </div>
                  <div className="item" style={{ userSelect: "none", height: "220px" }}>
                    <div>
                      <div
                        style={{
                          fontSize: "14px",
                          marginBottom: "8px",
                          textAlign: "center",
                          fontWeight: "bold",
                          padding: "8px 0",
                        }}
                      >
                        業務外作業時間に以下の作業データが含まれています
                      </div>
                      {labelBlock()}
                      <div
                        className="timeline"
                        style={{
                          border: "dashed 1px #b4e5e2",
                          backgroundColor: "#FFFFFF",
                          padding: "0",
                          height: "180px",
                          overflowY: "auto",
                          display: "flex",
                          flexFlow: "column",
                        }}
                      >
                        {
                          !notWorkingApplicationList.length && (
                            <div
                              style={{
                                height: "180px",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                fontSize: "20px",
                                color: "#AAAAAA",
                              }}
                            >
                              業務外作業時間に含まれる作業データはありません
                            </div>
                          )
                        }
                        {
                          notWorkingApplicationList.map((v) => (
                            <div
                              className="timeline_row"
                              key={uniqueId()}
                              style={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                position: "relative",
                                minHeight: "32px",
                                borderBottom: "1px solid #CCCCCC",
                              }}
                            >
                              {rulerBlock(false, 32)}
                              {
                                date && v.list.map((value) => (
                                  <div
                                    key={`timeline_${value.start_sec}`}
                                    className="timeline_bar not_working"
                                    style={{
                                      left: `${((value.start_sec - 18000) / 86400) * 100}%`,
                                      width:
                                      `${((value.end_sec - value.start_sec) / 86400) * 100 > 0.2 ? ((value.end_sec - value.start_sec) / 86400) * 100 : 0.2}%`,
                                      position: "absolute",
                                      top: "3px",
                                      height: "24px",
                                    }}
                                  >
                                    <Tips
                                      description={`${secToHourMinute(value.start_sec)}~${secToHourMinute(value.end_sec)}`}
                                      className=""
                                      icon={false}
                                    >
                                      <></>
                                    </Tips>
                                  </div>
                                ))
                              }
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "4px",
                                  marginLeft: "8px",
                                  fontSize: "10px",
                                  zIndex: 1,
                                }}
                                title={v.task_name || ""}
                              >
                                <div
                                  className="icon_area"
                                  style={{
                                    color: v.icon_color,
                                    fontSize: "12px",
                                  }}
                                >
                                  {
                                    v.icon && (
                                      <FontAwesomeIcon
                                        icon={[v.icon.split(" ")[0] as IconPrefix, v.icon.split(" ")[1] as IconName]}
                                        fixedWidth
                                      />
                                    )
                                  }
                                </div>
                                <div>
                                  {v.task_name}
                                </div>
                              </div>
                              <div
                                style={{
                                  display: "flex",
                                  gap: "4px",
                                  marginRight: "8px",
                                  zIndex: 1,
                                  fontSize: "10px",
                                  color: "#999999",
                                }}
                              >
                                <div>
                                  <FontAwesomeIcon icon={['far', 'clock']} />
                                </div>
                                <div>
                                  {buildDisplayTime(v.time)}
                                </div>
                              </div>
                            </div>
                          ))
                        }
                      </div>
                    </div>
                  </div>
                </div>
              </>
            )
          }
        </>
        <>
          <button
            type="button"
            className="button cancel"
            onClick={closeModal}
          >
            閉じる
          </button>
        </>
      </Modal>
    </>
  )
}

export default NotWorkingModalForDisplay
